// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — hallLayoutThumb.jsx   ·   A HALL, SEEN FROM ABOVE (OW-42)
// ────────────────────────────────────────────────────────────────────
// Plans for the shop and FURNISH: a layout drawn as two of its bays (what
// Ally sells), and a whole composed Hall drawn as a strip (a saved design,
// a friend's Hall). SVG, no canvas, no three — a card, not a scene.
// ⚑ A risen layout BREATHES here too (the Mythical rule: its Legendary,
// animated) — a CSS pulse on the walls, nothing else changes.
// ════════════════════════════════════════════════════════════════════

import { ITEMS } from "./mineriumCatalogue";
import { planOf } from "./hallInterior";

const FLOOR = "#221b2c";
const EDGE = "#4a3f5a";
const DOOR = "#c9a84c";
const tintOf = (fin, fallback) => (fin && fin !== 1 && ITEMS[fin]?.finish?.tint) || fallback;

const PULSE = `@keyframes hallThumbBreathe { 0%,100% { opacity: .75; filter: drop-shadow(0 0 0 #b388ff) } 50% { opacity: 1; filter: drop-shadow(0 0 2px #d8b8ff) } }`;

// ── a layout, as two bays and the gutter between them ────────────────
export function LayoutMinimap({ itemId, width = 150 }) {
  const plan = planOf(itemId);
  if (!plan) return null;
  const risen = !!ITEMS[itemId]?.ascendedFrom;
  const cols = 10 + 1 + 10, rows = plan.bay.length;
  const cell = width / (cols + 2);
  const wallTint = tintOf(plan.wall, "#d8d0e2");
  const rects = [];
  const put = (cx, cy, ch, paint) => {
    const x = (cx + 1) * cell, y = (cy + 1) * cell;
    const fill = plan.paint?.[paint] || plan.paint?.[ch];
    if (fill) rects.push(<rect key={`f${cx},${cy}`} x={x} y={y} width={cell} height={cell} fill={tintOf(fill, FLOOR)} opacity={0.55} />);
    if (ch === "#") rects.push(<rect key={`w${cx},${cy}`} className={risen ? "hallThumbWall" : undefined} x={x + cell * 0.12} y={y + cell * 0.12} width={cell * 0.76} height={cell * 0.76} fill={wallTint} rx={1} />);
    if (ch === "D") rects.push(<rect key={`d${cx},${cy}`} x={x + cell * 0.2} y={y + cell * 0.35} width={cell * 0.6} height={cell * 0.3} fill={DOOR} rx={1} />);
  };
  plan.bay.forEach((row, r) => {
    for (let i = 0; i < 10; i++) { put(i, r, row[i]); put(11 + i, r, row[i]); }
    put(10, r, plan.gutter?.[r] || ".", plan.gutterPaint?.[r]);
  });
  const H = (rows + 2) * cell;
  return (
    <svg width={width} height={H} viewBox={`0 0 ${width} ${H}`} role="img" aria-label={`${ITEMS[itemId]?.name} — plan`} style={{ display: "block" }}>
      {risen && <style>{`${PULSE} .hallThumbWall { animation: hallThumbBreathe 2.6s ease-in-out infinite }`}</style>}
      <rect x={0} y={0} width={width} height={H} fill="#0c0a12" rx={4} />
      <rect x={cell} y={cell} width={cols * cell} height={rows * cell} fill={FLOOR} stroke={EDGE} strokeWidth={1} />
      {/* the Wall of Renown along the top, the Gallery Wall along the bottom */}
      <rect x={cell} y={cell * 0.35} width={cols * cell} height={cell * 0.55} fill="#8a8298" />
      <rect x={cell} y={(rows + 1) * cell + cell * 0.1} width={cols * cell} height={cell * 0.55} fill="#8a8298" />
      {rects}
    </svg>
  );
}

// ── a whole Hall (composed), as one long strip ───────────────────────
export function InteriorMinimap({ hall, base, width = 220, wornTint = null }) {
  if (!hall || !base) return null;
  const x0 = 1, x1 = base.cols - 2, y0 = base.floorTop, y1 = base.floorBot;
  const cols = x1 - x0 + 1, rows = y1 - y0 + 1;
  const cell = width / cols;
  // ⚑ a cell never thinner than a pixel and a half — a small strip is stretched tall
  const H = Math.max(rows * cell, rows * 1.5) + 4;
  const ch = (H - 4) / rows;
  return (
    <svg width={width} height={H} viewBox={`0 0 ${width} ${H}`} role="img" aria-label="Hall plan" style={{ display: "block" }}>
      {hall.glow && <style>{`${PULSE} .hallThumbWall { animation: hallThumbBreathe 2.6s ease-in-out infinite }`}</style>}
      <rect x={0} y={0} width={width} height={H} fill={FLOOR} rx={3} />
      {hall.floors.map((f) => (
        <rect key={`f${f.x},${f.y}`} x={(f.x - x0) * cell} y={(f.y - y0) * ch + 2} width={cell} height={ch} fill={tintOf(f.finish, FLOOR)} opacity={0.55} />
      ))}
      <g className={hall.glow ? "hallThumbWall" : undefined}>
        {hall.walls.map((w) => (
          <rect key={`w${w.x},${w.y}`} x={(w.x - x0) * cell} y={(w.y - y0) * ch + 2} width={cell} height={ch} fill={tintOf(w.finish, wornTint || "#d8d0e2")} />
        ))}
      </g>
      {hall.doors.map((d) => (
        <rect key={`d${d.x},${d.y}`} x={(d.x - x0) * cell} y={(d.y - y0) * ch + 2} width={cell} height={ch} fill={DOOR} />
      ))}
    </svg>
  );
}
